import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ArrowDownTrayIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';
import { Alert, Button, Modal } from '../../components/common/UIComponents';
import { useProductFeedbackCapability } from '../../components/ProductFeedback/ProductFeedbackRuntimeProvider';
import {
  PRODUCT_FEEDBACK_PAGE_SIZE,
  getProductFeedbackStatusCounts,
  isValidProductFeedbackReportId,
  listProductFeedbackReports,
  readCanonicalProductFeedbackReport,
  readProductFeedbackDiagnostics,
  updateProductFeedbackReportStatus,
  type ProductFeedbackListFilter,
  type ProductFeedbackPageCursor,
  type ProductFeedbackStatusCounts,
} from '../../services/productFeedbackService';
import {
  PRODUCT_FEEDBACK_LIMITS,
  type ProductFeedbackDiagnosticsPayload,
  type ProductFeedbackReport,
  type ProductFeedbackStatus,
} from '../../types/productFeedback';
import {
  createProductFeedbackMarkdownBlob,
  createProductFeedbackMarkdownExport,
} from '../../utils/productFeedbackPresentation';
import { readProductFeedbackScreenshot } from '../../services/productFeedbackAttachmentService';
import { isSafeDocumentId, notificationService } from '../../services/notificationService';
import {
  getProductFeedbackCategoryLabel,
  getProductFeedbackStatusLabel,
} from '../../utils/productFeedbackUi';
import { normalizeProductFeedbackTimestamp } from '../../utils/productFeedbackTimestamp';

const FILTERS: Array<{ value: ProductFeedbackListFilter; label: string }> = [
  { value: 'open', label: 'Otwarte' },
  { value: 'investigating', label: 'W analizie' },
  { value: 'resolved', label: 'Rozwiązane' },
  { value: 'all', label: 'Wszystkie' },
];

const useCompactAdminFeedback = (): boolean => {
  const [compact, setCompact] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia('(max-width: 1023px)').matches
  );
  useEffect(() => {
    const media = window.matchMedia('(max-width: 1023px)');
    const update = () => setCompact(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);
  return compact;
};

const UTC_DATE_TIME_FORMATTER = new Intl.DateTimeFormat('pl-PL', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'UTC',
});

const formatUtc = (value: unknown): string => {
  const iso = normalizeProductFeedbackTimestamp(value);
  return iso ? `${UTC_DATE_TIME_FORMATTER.format(new Date(iso))} UTC` : 'Nie podano.';
};

const optionalText = (value: string | null): React.ReactNode =>
  value ? (
    <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-primary-light-200">
      {value}
    </p>
  ) : (
    <p className="text-sm text-primary-light-400">Nie podano.</p>
  );

interface InspectorProps {
  report: ProductFeedbackReport | null;
  loading: boolean;
  error: string | null;
  diagnostics: ProductFeedbackDiagnosticsPayload | null;
  diagnosticsLoading: boolean;
  diagnosticsError: string | null;
  attachmentUrl: string | null;
  attachmentLoading: boolean;
  attachmentError: string | null;
  mutationPending: boolean;
  mutationError: string | null;
  onRetryReport: () => void;
  onRetryDiagnostics: () => void;
  onRetryAttachment: () => void;
  onTransition: (status: ProductFeedbackStatus, trigger: HTMLButtonElement) => void;
  onResolve: (trigger: HTMLButtonElement) => void;
  onDownload: () => void;
  closeButtonRef?: React.RefObject<HTMLButtonElement>;
  onClose?: () => void;
}

const ProductFeedbackInspector: React.FC<InspectorProps> = ({
  report,
  loading,
  error,
  diagnostics,
  diagnosticsLoading,
  diagnosticsError,
  attachmentUrl,
  attachmentLoading,
  attachmentError,
  mutationPending,
  mutationError,
  onRetryReport,
  onRetryDiagnostics,
  onRetryAttachment,
  onTransition,
  onResolve,
  onDownload,
  closeButtonRef,
  onClose,
}) => {
  if (loading) return <p className="p-4 text-sm text-primary-light-300">Ładowanie raportu…</p>;
  if (error)
    return (
      <div className="space-y-3 p-4">
        <Alert variant="error">{error}</Alert>
        <Button type="button" variant="neutral" onClick={onRetryReport}>
          Ponów odczyt raportu
        </Button>
      </div>
    );
  if (!report)
    return <p className="p-4 text-sm text-primary-light-400">Wybierz raport z kolejki.</p>;

  const downloadReady = Boolean(diagnostics && !diagnosticsLoading && !diagnosticsError);
  return (
    <div className="min-w-0 space-y-5 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-all text-xs text-primary-light-400">{report.id}</p>
          <h2 className="mt-1 break-words text-lg font-semibold text-primary-light-100">
            {getProductFeedbackCategoryLabel(report.category)}
          </h2>
          <p className="mt-1 text-sm font-medium text-ratuj-200">
            {getProductFeedbackStatusLabel(report.status)}
          </p>
        </div>
        {onClose && (
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="min-h-11 min-w-11 rounded-lg text-primary-light-300 hover:bg-primary-dark-100/60"
            aria-label="Zamknij inspektor"
          >
            ×
          </button>
        )}
      </div>

      {mutationError && <Alert variant="error">{mutationError}</Alert>}
      <div className="flex flex-wrap gap-2">
        {report.status === 'open' && (
          <>
            <Button
              type="button"
              disabled={mutationPending}
              isLoading={mutationPending}
              onClick={(event) => onTransition('investigating', event.currentTarget)}
            >
              Rozpocznij analizę
            </Button>
            <Button
              type="button"
              variant="neutral"
              disabled={mutationPending}
              onClick={(event) => onResolve(event.currentTarget)}
            >
              Rozwiąż
            </Button>
          </>
        )}
        {report.status === 'investigating' && (
          <Button
            type="button"
            disabled={mutationPending}
            isLoading={mutationPending}
            onClick={(event) => onResolve(event.currentTarget)}
          >
            Rozwiąż
          </Button>
        )}
        {report.status === 'resolved' && (
          <Button
            type="button"
            disabled={mutationPending}
            isLoading={mutationPending}
            onClick={(event) => onTransition('investigating', event.currentTarget)}
          >
            Wznów analizę
          </Button>
        )}
        <Button
          type="button"
          variant="neutral"
          icon={<ArrowDownTrayIcon className="h-4 w-4" />}
          disabled={!downloadReady || mutationPending}
          onClick={onDownload}
        >
          Pobierz Markdown
        </Button>
      </div>

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary-light-400">
          Opis
        </h3>
        {optionalText(report.description)}
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary-light-400">
          Zrzut ekranu
        </h3>
        {!report.attachment && report.attachmentRequested ? (
          <p className="text-sm text-status-warning-400">
            Zrzut był wymagany, ale nie został canonical potwierdzony.
          </p>
        ) : !report.attachment ? (
          <p className="text-sm text-primary-light-400">Nie dołączono zrzutu.</p>
        ) : attachmentLoading ? (
          <p className="text-sm text-primary-light-300">Ładowanie zrzutu…</p>
        ) : attachmentError ? (
          <div className="space-y-2">
            <Alert variant="error">{attachmentError}</Alert>
            <Button type="button" size="sm" variant="neutral" onClick={onRetryAttachment}>
              Ponów odczyt zrzutu
            </Button>
          </div>
        ) : attachmentUrl ? (
          <div className="space-y-2">
            <img
              src={attachmentUrl}
              alt="Zrzut ekranu dołączony do raportu"
              className="max-h-64 max-w-full rounded-lg object-contain"
            />
            <a
              href={attachmentUrl}
              download={`${report.id}-screenshot.jpg`}
              className="inline-flex min-h-11 items-center text-sm font-medium text-ratuj-200 underline"
            >
              Pobierz zrzut
            </a>
            <div className="space-y-1 text-xs text-primary-light-500">
              <p>{report.attachment.bytes} B · JPEG</p>
              <p className="break-all">Generacja: {report.attachment.generation}</p>
              <p>Potwierdzono: {formatUtc(report.attachment.attachedAt)}</p>
            </div>
          </div>
        ) : null}
      </section>
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary-light-400">
          Oczekiwane zachowanie
        </h3>
        {optionalText(report.expectedBehavior)}
      </section>
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary-light-400">
          Rzeczywiste zachowanie
        </h3>
        {optionalText(report.actualBehavior)}
      </section>
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary-light-400">
          Kroki odtworzenia
        </h3>
        {optionalText(report.reproductionNotes)}
      </section>

      <section className="space-y-1 text-sm text-primary-light-300">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary-light-400">
          Środowisko
        </h3>
        <p className="break-words">Trasa: {report.runtime.routePath}</p>
        <p>
          Viewport: {report.runtime.viewport.width}×{report.runtime.viewport.height}
        </p>
        <p>Online: {report.runtime.online ? 'tak' : 'nie'}</p>
        <p className="break-words">Aplikacja: {report.runtime.appVersion}</p>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary-light-400">
          Diagnostyka
        </h3>
        {diagnosticsLoading ? (
          <p className="text-sm text-primary-light-300">Ładowanie diagnostyki…</p>
        ) : diagnosticsError ? (
          <div className="space-y-2">
            <Alert variant="error">{diagnosticsError}</Alert>
            <Button type="button" size="sm" variant="neutral" onClick={onRetryDiagnostics}>
              Ponów odczyt diagnostyki
            </Button>
          </div>
        ) : diagnostics && !diagnostics.included ? (
          <p className="text-sm text-primary-light-400">
            Reporter świadomie nie dołączył diagnostyki.
          </p>
        ) : diagnostics ? (
          <div className="space-y-3">
            <p className="text-xs text-primary-light-400">
              {diagnostics.events.length} zdarzeń · {diagnostics.bytes} B · redakcje:{' '}
              {diagnostics.clientRedactionCount + diagnostics.serverRedactionCount}
            </p>
            {diagnostics.events.map((event, index) => (
              <article
                key={`${event.occurredAtClient}-${index}`}
                className="min-w-0 rounded-lg bg-primary-dark-100/55 p-3 text-xs"
              >
                <p className="font-semibold text-primary-light-200">
                  {event.level.toUpperCase()} · {event.source}
                </p>
                <p className="mt-1 break-words text-primary-light-400">
                  {event.occurredAtClient} · {event.routePath}
                </p>
                <p className="mt-2 whitespace-pre-wrap break-words text-primary-light-300">
                  {event.message}
                </p>
                {event.details.map((detail, detailIndex) => (
                  <p
                    key={detailIndex}
                    className="mt-1 whitespace-pre-wrap break-words text-primary-light-400"
                  >
                    {detail}
                  </p>
                ))}
                {event.stack && (
                  <pre className="mt-2 whitespace-pre-wrap break-all text-primary-light-400">
                    {event.stack}
                  </pre>
                )}
              </article>
            ))}
          </div>
        ) : null}
      </section>

      <section className="space-y-1 text-sm text-primary-light-300">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary-light-400">
          Rozwiązanie
        </h3>
        {optionalText(report.resolutionNote)}
        {report.status === 'resolved' && (
          <p className="text-xs text-primary-light-400">
            Rozwiązano: {formatUtc(report.resolvedAt)}
          </p>
        )}
      </section>
      <section className="space-y-1 border-t border-primary-dark-300/55 pt-4 text-xs text-primary-light-400">
        <p>Utworzono: {formatUtc(report.createdAt)}</p>
        <p>Zmieniono: {formatUtc(report.updatedAt)}</p>
        <p>Wersja statusu: {report.statusVersion}</p>
        <p className="break-all">Reporter UID: {report.createdBy}</p>
      </section>
    </div>
  );
};

const AdminProductFeedbackPage: React.FC = () => {
  const capability = useProductFeedbackCapability();
  const location = useLocation();
  const compact = useCompactAdminFeedback();
  const inspectorTitleId = useId();
  const resolutionTitleId = useId();
  const selectionReturnRef = useRef<HTMLElement | null>(null);
  const mobileCloseRef = useRef<HTMLButtonElement | null>(null);
  const resolutionCancelRef = useRef<HTMLButtonElement | null>(null);
  const statusActionRef = useRef<HTMLElement | null>(null);
  const attachmentUrlRef = useRef<string | null>(null);
  const accessGenerationRef = useRef(0);
  const listGenerationRef = useRef(0);
  const loadGenerationRef = useRef(0);
  const mutationGenerationRef = useRef(0);
  const notificationTarget = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const reportId = String(params.get('reportId') || '').trim();
    const notificationId = String(params.get('notificationId') || '').trim();
    return {
      reportId: isValidProductFeedbackReportId(reportId) ? reportId : '',
      notificationId: isSafeDocumentId(notificationId) ? notificationId : '',
    };
  }, [location.search]);
  const [filter, setFilter] = useState<ProductFeedbackListFilter>('open');
  const [counts, setCounts] = useState<ProductFeedbackStatusCounts | null>(null);
  const [countsError, setCountsError] = useState<string | null>(null);
  const [reports, setReports] = useState<ProductFeedbackReport[]>([]);
  const [cursor, setCursor] = useState<ProductFeedbackPageCursor | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(notificationTarget.reportId || null);
  const [selectedReport, setSelectedReport] = useState<ProductFeedbackReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [targetUnavailable, setTargetUnavailable] = useState(false);
  const [diagnostics, setDiagnostics] = useState<ProductFeedbackDiagnosticsPayload | null>(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [diagnosticsError, setDiagnosticsError] = useState<string | null>(null);
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mutationPending, setMutationPending] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');

  const clearAttachmentUrl = useCallback(() => {
    if (attachmentUrlRef.current) URL.revokeObjectURL(attachmentUrlRef.current);
    attachmentUrlRef.current = null;
    setAttachmentUrl(null);
  }, []);

  useEffect(
    () => () => {
      if (attachmentUrlRef.current) URL.revokeObjectURL(attachmentUrlRef.current);
    },
    []
  );

  const loadAttachment = useCallback(
    async (report: ProductFeedbackReport, generation = loadGenerationRef.current) => {
      clearAttachmentUrl();
      setAttachmentError(null);
      if (!report.attachment) {
        setAttachmentLoading(false);
        return;
      }
      setAttachmentLoading(true);
      try {
        const blob = await readProductFeedbackScreenshot(report.id);
        if (generation !== loadGenerationRef.current || !capability.isAllowed) return;
        const url = URL.createObjectURL(blob);
        attachmentUrlRef.current = url;
        setAttachmentUrl(url);
      } catch (error) {
        if (generation !== loadGenerationRef.current || !capability.isAllowed) return;
        const code = String((error as { code?: unknown })?.code || '');
        setAttachmentError(
          code.includes('unauthorized') || code.includes('permission-denied')
            ? 'Dostęp do zrzutu został cofnięty lub nie jest autoryzowany.'
            : code.includes('object-not-found')
              ? 'Canonical metadane istnieją, ale zrzut jest niedostępny.'
              : 'Nie udało się odczytać zrzutu.'
        );
      } finally {
        if (generation === loadGenerationRef.current) setAttachmentLoading(false);
      }
    },
    [capability.isAllowed, clearAttachmentUrl]
  );

  const loadCounts = useCallback(async () => {
    const accessGeneration = accessGenerationRef.current;
    try {
      setCountsError(null);
      const nextCounts = await getProductFeedbackStatusCounts();
      if (accessGeneration !== accessGenerationRef.current) return;
      setCounts(nextCounts);
    } catch {
      if (accessGeneration !== accessGenerationRef.current) return;
      setCounts(null);
      setCountsError('Nie udało się pobrać dokładnych liczników.');
    }
  }, []);

  const loadFirstPage = useCallback(async () => {
    const accessGeneration = accessGenerationRef.current;
    const listGeneration = ++listGenerationRef.current;
    setListLoading(true);
    setListError(null);
    setPageError(null);
    try {
      const page = await listProductFeedbackReports(filter);
      if (
        accessGeneration !== accessGenerationRef.current ||
        listGeneration !== listGenerationRef.current
      )
        return;
      setReports(page.reports);
      setCursor(page.nextCursor);
      if (page.reports[0] && !compact) setSelectedId((current) => current || page.reports[0].id);
    } catch {
      if (
        accessGeneration !== accessGenerationRef.current ||
        listGeneration !== listGenerationRef.current
      )
        return;
      setReports([]);
      setCursor(null);
      setListError('Nie udało się pobrać kolejki raportów.');
    } finally {
      if (
        accessGeneration === accessGenerationRef.current &&
        listGeneration === listGenerationRef.current
      )
        setListLoading(false);
    }
  }, [compact, filter]);

  useEffect(() => {
    if (!capability.isAllowed) {
      accessGenerationRef.current += 1;
      listGenerationRef.current += 1;
      loadGenerationRef.current += 1;
      mutationGenerationRef.current += 1;
      setReports([]);
      setCounts(null);
      setCountsError(null);
      setCursor(null);
      setListLoading(false);
      setListError(null);
      setPageLoading(false);
      setPageError(null);
      setSelectedId(null);
      setSelectedReport(null);
      setReportLoading(false);
      setReportError(null);
      setTargetUnavailable(false);
      setDiagnostics(null);
      setDiagnosticsLoading(false);
      setDiagnosticsError(null);
      clearAttachmentUrl();
      setAttachmentLoading(false);
      setAttachmentError(null);
      setMobileOpen(false);
      setResolveOpen(false);
      setMutationPending(false);
      setMutationError(null);
      return;
    }
    void loadCounts();
    void loadFirstPage();
  }, [capability.isAllowed, clearAttachmentUrl, loadCounts, loadFirstPage]);

  useEffect(() => {
    if (!capability.isAllowed || !notificationTarget.reportId) return;
    setTargetUnavailable(false);
    setSelectedId(notificationTarget.reportId);
  }, [capability.isAllowed, notificationTarget.reportId]);

  const loadSelected = useCallback(async () => {
    if (!selectedId || !capability.isAllowed) return;
    const generation = ++loadGenerationRef.current;
    setReportLoading(true);
    setReportError(null);
    setTargetUnavailable(false);
    setSelectedReport(null);
    setDiagnostics(null);
    setDiagnosticsError(null);
    setDiagnosticsLoading(false);
    clearAttachmentUrl();
    setAttachmentLoading(false);
    setAttachmentError(null);
    try {
      const report = await readCanonicalProductFeedbackReport(selectedId);
      if (generation !== loadGenerationRef.current || !capability.isAllowed) return;
      setSelectedReport(report);
      setReportLoading(false);
      if (notificationTarget.reportId === report.id && notificationTarget.notificationId) {
        try {
          await notificationService.markProductFeedbackNotificationAsRead({
            notificationId: notificationTarget.notificationId,
            reportId: report.id,
          });
        } catch (error) {
          console.error('Failed to mark Product Feedback notification as read:', error);
        }
      }
      if (compact && notificationTarget.reportId === report.id) {
        setMobileOpen(true);
      }
      void loadAttachment(report, generation);
      setDiagnosticsLoading(true);
      try {
        const payload = await readProductFeedbackDiagnostics(selectedId);
        if (generation === loadGenerationRef.current && capability.isAllowed)
          setDiagnostics(payload);
      } catch {
        if (generation === loadGenerationRef.current)
          setDiagnosticsError('Raport jest dostępny, ale nie udało się odczytać jego diagnostyki.');
      } finally {
        if (generation === loadGenerationRef.current) setDiagnosticsLoading(false);
      }
    } catch (error) {
      if (generation === loadGenerationRef.current) {
        const isTargetMissing =
          selectedId === notificationTarget.reportId &&
          error instanceof Error &&
          error.message === 'Canonical Product Feedback report does not exist.';
        setTargetUnavailable(isTargetMissing);
        setReportError(isTargetMissing ? null : 'Nie udało się pobrać canonical raportu.');
        setReportLoading(false);
      }
    }
  }, [
    capability.isAllowed,
    clearAttachmentUrl,
    compact,
    loadAttachment,
    notificationTarget.notificationId,
    notificationTarget.reportId,
    selectedId,
  ]);

  useEffect(() => {
    void loadSelected();
  }, [loadSelected]);
  useEffect(() => {
    if (!compact) setMobileOpen(false);
  }, [compact]);

  const loadNextPage = async () => {
    if (!cursor || pageLoading || !capability.isAllowed) return;
    const accessGeneration = accessGenerationRef.current;
    const listGeneration = listGenerationRef.current;
    setPageLoading(true);
    setPageError(null);
    try {
      const page = await listProductFeedbackReports(filter, cursor);
      if (
        accessGeneration !== accessGenerationRef.current ||
        listGeneration !== listGenerationRef.current
      )
        return;
      setReports((current) => [...current, ...page.reports]);
      setCursor(page.nextCursor);
    } catch {
      if (
        accessGeneration !== accessGenerationRef.current ||
        listGeneration !== listGenerationRef.current
      )
        return;
      setPageError('Nie udało się pobrać następnej strony.');
    } finally {
      if (
        accessGeneration === accessGenerationRef.current &&
        listGeneration === listGenerationRef.current
      )
        setPageLoading(false);
    }
  };

  const retryDiagnostics = async () => {
    if (!selectedId || !capability.isAllowed) return;
    const accessGeneration = accessGenerationRef.current;
    const loadGeneration = loadGenerationRef.current;
    setDiagnosticsLoading(true);
    setDiagnosticsError(null);
    try {
      const payload = await readProductFeedbackDiagnostics(selectedId);
      if (
        accessGeneration !== accessGenerationRef.current ||
        loadGeneration !== loadGenerationRef.current
      )
        return;
      setDiagnostics(payload);
    } catch {
      if (
        accessGeneration !== accessGenerationRef.current ||
        loadGeneration !== loadGenerationRef.current
      )
        return;
      setDiagnosticsError('Raport jest dostępny, ale nie udało się odczytać jego diagnostyki.');
    } finally {
      if (
        accessGeneration === accessGenerationRef.current &&
        loadGeneration === loadGenerationRef.current
      )
        setDiagnosticsLoading(false);
    }
  };

  const mutateStatus = async (nextStatus: ProductFeedbackStatus, note?: string | null) => {
    if (!selectedReport || !capability.isAllowed || mutationPending) return;
    const source = selectedReport;
    const generation = ++mutationGenerationRef.current;
    setMutationPending(true);
    setMutationError(null);
    try {
      const result = await updateProductFeedbackReportStatus({
        reportId: source.id,
        expectedStatus: source.status,
        expectedStatusVersion: source.statusVersion,
        nextStatus,
        ...(nextStatus === 'resolved' ? { resolutionNote: note || null } : {}),
      });
      if (generation !== mutationGenerationRef.current || !capability.isAllowed) return;
      const canonical = await readCanonicalProductFeedbackReport(source.id);
      if (generation !== mutationGenerationRef.current || !capability.isAllowed) return;
      if (
        canonical.status !== result.status ||
        canonical.statusVersion !== result.statusVersion ||
        canonical.status !== nextStatus
      )
        throw new Error('Canonical status mismatch');
      setSelectedReport(canonical);
      setReports((current) =>
        current
          .map((item) => (item.id === canonical.id ? canonical : item))
          .filter((item) => filter === 'all' || item.status === filter)
      );
      setResolveOpen(false);
      setResolutionNote('');
      await loadCounts();
    } catch (error) {
      if (generation !== mutationGenerationRef.current) return;
      const code = String((error as { code?: unknown })?.code || '');
      setMutationError(
        code.includes('aborted')
          ? 'Raport zmienił się od ostatniego odczytu. Odśwież canonical stan.'
          : 'Nie udało się potwierdzić canonical zmiany statusu.'
      );
    } finally {
      if (generation === mutationGenerationRef.current) setMutationPending(false);
    }
  };

  const requestResolve = (trigger: HTMLButtonElement) => {
    statusActionRef.current = trigger;
    setMutationError(null);
    setResolutionNote(selectedReport?.resolutionNote || '');
    if (compact) setMobileOpen(false);
    setResolveOpen(true);
  };

  const downloadMarkdown = () => {
    if (!selectedReport || !diagnostics) return;
    const download = createProductFeedbackMarkdownExport({ report: selectedReport, diagnostics });
    const url = URL.createObjectURL(createProductFeedbackMarkdownBlob(download));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = download.filename;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const unresolved = counts ? counts.open + counts.investigating : null;
  const inspector = (
    <ProductFeedbackInspector
      report={selectedReport}
      loading={reportLoading}
      error={reportError}
      diagnostics={diagnostics}
      diagnosticsLoading={diagnosticsLoading}
      diagnosticsError={diagnosticsError}
      attachmentUrl={attachmentUrl}
      attachmentLoading={attachmentLoading}
      attachmentError={attachmentError}
      mutationPending={mutationPending}
      mutationError={mutationError}
      onRetryReport={() => void loadSelected()}
      onRetryDiagnostics={() => void retryDiagnostics()}
      onRetryAttachment={() => selectedReport && void loadAttachment(selectedReport)}
      onTransition={(status, trigger) => {
        statusActionRef.current = trigger;
        void mutateStatus(status);
      }}
      onResolve={requestResolve}
      onDownload={downloadMarkdown}
    />
  );

  if (capability.status === 'checking')
    return (
      <p className="py-8 text-sm text-primary-light-300">
        Sprawdzanie dostępu do Feedback i diagnostyki…
      </p>
    );
  if (!capability.isAllowed)
    return (
      <Alert variant="error">
        Dostęp do Feedback i diagnostyki jest niedostępny. Uprawnienie nie zostało potwierdzone.
      </Alert>
    );

  return (
    <div className="min-w-0 space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-ratuj-300">
            Operacje administracyjne
          </p>
          <h1 className="mt-1 text-xl font-semibold text-primary-light-100">
            Feedback i diagnostyka
          </h1>
          <p className="mt-1 text-sm text-primary-light-300">
            {unresolved == null
              ? 'Liczba wymagających uwagi jest nieznana.'
              : `${unresolved} raportów wymaga uwagi.`}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2" aria-label="Dokładne liczniki statusów">
          {(['open', 'investigating', 'resolved'] as ProductFeedbackStatus[]).map((status) => (
            <div key={status} className="rounded-lg bg-primary-dark-200/75 px-3 py-2 text-center">
              <span className="block text-lg font-semibold text-primary-light-100">
                {counts ? counts[status] : '—'}
              </span>
              <span className="text-[11px] text-primary-light-400">
                {getProductFeedbackStatusLabel(status)}
              </span>
            </div>
          ))}
        </div>
      </header>
      {countsError && (
        <Alert variant="warning">
          {countsError}{' '}
          <button className="underline" onClick={() => void loadCounts()}>
            Ponów
          </button>
        </Alert>
      )}
      {targetUnavailable && (
        <Alert variant="warning">
          Wskazany raport nie jest już dostępny. Powiadomienie pozostaje nieprzeczytane.
        </Alert>
      )}

      <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Filtr statusu">
        {FILTERS.map((item) => (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={filter === item.value}
            onClick={() => {
              setFilter(item.value);
              setSelectedId(null);
              setSelectedReport(null);
              setDiagnostics(null);
            }}
            className={`min-h-11 shrink-0 rounded-lg px-3 text-sm font-medium ${filter === item.value ? 'bg-ratuj-300/18 text-ratuj-200 ring-1 ring-ratuj-300/25' : 'text-primary-light-300 hover:bg-primary-dark-200/65'}`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(22rem,0.8fr)]">
        <section
          className="min-w-0 overflow-hidden rounded-lg border border-primary-dark-300/55 bg-primary-dark-200/55"
          aria-label="Kolejka raportów"
        >
          {listLoading ? (
            <p className="p-4 text-sm text-primary-light-300">Ładowanie kolejki…</p>
          ) : listError ? (
            <div className="space-y-3 p-4">
              <Alert variant="error">{listError}</Alert>
              <Button type="button" variant="neutral" onClick={() => void loadFirstPage()}>
                Ponów
              </Button>
            </div>
          ) : reports.length === 0 ? (
            <p className="p-6 text-center text-sm text-primary-light-400">
              Brak raportów dla aktywnego filtra.
            </p>
          ) : (
            <div className="divide-y divide-primary-dark-300/45">
              {reports.map((report) => (
                <button
                  key={report.id}
                  type="button"
                  aria-pressed={selectedId === report.id}
                  onClick={(event) => {
                    selectionReturnRef.current = event.currentTarget;
                    setSelectedId(report.id);
                    if (compact) setMobileOpen(true);
                  }}
                  className={`flex min-h-20 w-full min-w-0 items-start gap-3 px-3 py-3 text-left hover:bg-primary-dark-100/45 ${selectedId === report.id ? 'bg-ratuj-300/8' : ''}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold text-ratuj-200">
                        {getProductFeedbackStatusLabel(report.status)}
                      </span>
                      <span className="text-xs text-primary-light-400">
                        {getProductFeedbackCategoryLabel(report.category)}
                      </span>
                    </div>
                    <p className="mt-1 line-clamp-2 break-words text-sm text-primary-light-200">
                      {report.description}
                    </p>
                    <p className="mt-1 break-all text-[11px] text-primary-light-500">
                      {report.id} · {formatUtc(report.createdAt)}
                    </p>
                  </div>
                  <ChevronRightIcon className="mt-1 h-4 w-4 shrink-0 text-primary-light-500" />
                </button>
              ))}
            </div>
          )}
          {cursor && !listLoading && (
            <div className="border-t border-primary-dark-300/55 p-3">
              <Button
                type="button"
                variant="neutral"
                fullWidth
                disabled={pageLoading}
                isLoading={pageLoading}
                onClick={() => void loadNextPage()}
              >
                Załaduj następne {PRODUCT_FEEDBACK_PAGE_SIZE}
              </Button>
              {pageError && (
                <div className="mt-2">
                  <Alert variant="error">{pageError}</Alert>
                </div>
              )}
            </div>
          )}
        </section>
        {!compact && (
          <aside
            className="sticky top-20 max-h-[calc(100dvh-7rem)] min-w-0 overflow-y-auto rounded-lg border border-primary-dark-300/55 bg-primary-dark-200/55"
            aria-label="Inspektor raportu"
          >
            {inspector}
          </aside>
        )}
      </div>

      <Modal
        isOpen={compact && mobileOpen && Boolean(selectedId)}
        onClose={() => !mutationPending && setMobileOpen(false)}
        ariaLabelledBy={inspectorTitleId}
        initialFocusRef={mobileCloseRef}
        returnFocusRef={selectionReturnRef}
        placement="bottom-responsive"
        className="rounded-b-none sm:rounded-lg"
      >
        <span id={inspectorTitleId} className="sr-only">
          Inspektor raportu Product Feedback
        </span>
        <ProductFeedbackInspector
          {...(inspector.props as InspectorProps)}
          closeButtonRef={mobileCloseRef}
          onClose={() => setMobileOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={resolveOpen && Boolean(selectedReport)}
        onClose={() => !mutationPending && setResolveOpen(false)}
        dialogRole="alertdialog"
        ariaLabelledBy={resolutionTitleId}
        initialFocusRef={resolutionCancelRef}
        returnFocusRef={statusActionRef}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void mutateStatus('resolved', resolutionNote);
          }}
          className="space-y-4 p-4 sm:p-5"
        >
          <div>
            <h2 id={resolutionTitleId} className="text-lg font-semibold text-primary-light-100">
              Rozwiązać raport?
            </h2>
            <p className="mt-2 text-sm text-primary-light-300">
              Status i opcjonalna notatka zostaną zapisane wyłącznie przez canonical callable.
            </p>
          </div>
          <label className="block text-sm font-medium text-primary-light-200">
            Notatka rozwiązania (opcjonalna)
            <textarea
              value={resolutionNote}
              onChange={(event) => setResolutionNote(event.target.value)}
              maxLength={PRODUCT_FEEDBACK_LIMITS.maxResolutionNoteCharacters}
              disabled={mutationPending}
              rows={4}
              className="mt-1 block w-full rounded-lg border border-primary-dark-300/70 bg-primary-dark-100/80 px-3 py-2 text-sm text-primary-light-100 focus:ring-2 focus:ring-ratuj-300/70"
            />
          </label>
          {mutationError && <Alert variant="error">{mutationError}</Alert>}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              ref={resolutionCancelRef}
              type="button"
              variant="neutral"
              disabled={mutationPending}
              onClick={() => setResolveOpen(false)}
            >
              Anuluj
            </Button>
            <Button type="submit" disabled={mutationPending} isLoading={mutationPending}>
              Potwierdź rozwiązanie
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminProductFeedbackPage;
